import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInventoryEventBulkActionsState } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions/InventoryEventBulkActions/useInventoryEventBulkActionsState';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { Content } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { ModalContext } from 'src/contexts/ModalContext';
import {
  useSetNewGroupId,
  useSetOpenAutoPricingForGroupId,
} from 'src/contexts/NewGroupScrollContext/NewGroupScroll.atoms';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { Stack } from 'src/core/ui';
import { useListingGroupSellerAccountSettings } from 'src/hooks/api/useListingGroupSellerAccountSettings';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { AddToGroupListingsModal } from 'src/modals/GroupListings';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import {
  MoveListingGroupDialog,
  MoveListingGroupItem,
} from 'src/modals/GroupListingsV2/components/GroupsPreview/MoveListingGroupDialog';
import { ContentId } from 'src/utils/constants/contentId';
import {
  CommandOptionType,
  EventWithData,
  Feature,
  Listing,
  ListingGroup,
  ListingGroupItemInput,
} from 'src/WebApiController';

export const useBulkMoveToGroup = (eventData: EventWithData) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const { setModal } = useContext(ModalContext);

  const { openDialog, closeDialog } = useDialog(
    DialogId.AddToGroup,
    MoveListingGroupDialog
  );

  const hasSelectGroupAfterCreationFeature = useUserHasFeature(
    Feature.SelectGroupAfterCreation
  );
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const { data: accountDefaults } = useListingGroupSellerAccountSettings();

  const setNewGroupId = useSetNewGroupId();
  const setOpenAutoPricingForGroupId = useSetOpenAutoPricingForGroupId();

  const { filterQueryWithEventIds, isAllListingsSelected } =
    useInventoryEventBulkActionsState(eventData.event);

  const eventListings = useMemo(() => {
    return (
      (eventData?.entities?.listings?.flatMap(
        flattenListingGroup
      ) as Listing[]) ?? []
    );
  }, [eventData?.entities?.listings]);

  const selectedListingIds = useMemo(() => {
    if (isAllListingsSelected) {
      return filterQueryWithEventIds.entityIds?.length
        ? filterQueryWithEventIds.entityIds
        : eventListings.map((listing) => listing.id);
    }
    return filterQueryWithEventIds.entityIds ?? [];
  }, [eventListings, filterQueryWithEventIds.entityIds, isAllListingsSelected]);

  const eventListingGroups = useMemo(() => {
    if (!eventData?.entities?.listings) {
      return [];
    }

    return eventData.entities.listings
      .filter((l): l is ListingGroup => l.isLtGrp && l.ltGrpId != null)
      .map((group) => {
        const items = flattenListingGroup(group).map(
          (item) =>
            ({
              listingId: item.id,
              priority: item.ltGrpPrior,
              groupId: item.ltGrpId,
            }) as ListingGroupItemInput
        );
        return {
          listingGroupId: group.ltGrpId!,
          name: group.groupName,
          listingGroupItems: items,
        } as MoveListingGroupItem;
      });
  }, [eventData?.entities?.listings]);

  const onGroupCreatedCallback = useCallback(
    (groupId: string) => {
      if (hasSelectGroupAfterCreationFeature) {
        setNewGroupId(groupId);
        if (hasAutoPricingFeature) {
          setOpenAutoPricingForGroupId(groupId);
        }
      }
    },
    [
      hasAutoPricingFeature,
      hasSelectGroupAfterCreationFeature,
      setNewGroupId,
      setOpenAutoPricingForGroupId,
    ]
  );

  /**
   * Builds the AddToGroupListingsModal for a given group selection.
   * When this is a NEW group (not existing), inject account-level defaults
   * for desiredActiveListings, pricingSettingsJson (rank premium),
   * deprioritizedQuantities, and defaultSortId.
   */
  const buildAddToGroupModal = useCallback(
    (
      groupId: string,
      groupName?: string,
      inheritListingGroupSettings?: boolean,
      maintainPriceStagger?: boolean
    ) => {
      const existingGroup = eventListingGroups.find(
        (group) => group.listingGroupId === groupId
      );
      const newListingGroupName = existingGroup ? undefined : groupName;
      const isNewGroup = !existingGroup;

      // When creating a new group, apply account-level defaults for
      // desiredActiveListings, pricing settings, deprioritized quantities, and sort order
      const accountDesiredActiveListings =
        isNewGroup && accountDefaults?.desiredActiveListings != null
          ? accountDefaults.desiredActiveListings
          : undefined;

      const accountPricingSettingsJson = isNewGroup
        ? (accountDefaults?.pricingSettingsJson ?? undefined)
        : undefined;

      const accountDeprioritizedQuantities = isNewGroup
        ? (accountDefaults?.deprioritizedQuantities ?? undefined)
        : undefined;

      const accountDefaultSortId = isNewGroup
        ? (accountDefaults?.defaultSortId ?? undefined)
        : undefined;

      closeDialog();
      setModal({
        children: (
          <AddToGroupListingsModal
            selectedListingIds={selectedListingIds}
            listingGroupId={groupId ?? undefined}
            viagVirtId={eventData.event.virtId}
            newListingGroupName={newListingGroupName}
            inheritListingGroupSettings={inheritListingGroupSettings}
            maintainPriceStagger={maintainPriceStagger}
            defaultDesiredActiveListings={accountDesiredActiveListings}
            defaultPricingSettingsJson={accountPricingSettingsJson}
            defaultDeprioritizedQuantities={accountDeprioritizedQuantities}
            defaultSortId={accountDefaultSortId}
            onGroupCreated={onGroupCreatedCallback}
          />
        ),
        clickOutsideToClose: true,
        size: 'slide-in',
      });
    },
    [
      accountDefaults,
      closeDialog,
      eventData.event.virtId,
      eventListingGroups,
      onGroupCreatedCallback,
      selectedListingIds,
      setModal,
    ]
  );

  const onLaunchDialog = useCallback(
    (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      openDialog({
        listingGroups: eventListingGroups,
        listings: eventListings,
        canAddNewGroup: true,
        onMoveListingToGroup: buildAddToGroupModal,
        onClose: () => {
          closeDialog();
        },
      });
    },
    [
      buildAddToGroupModal,
      closeDialog,
      eventListingGroups,
      eventListings,
      openDialog,
    ]
  );

  const onLaunchDialogStableRef = useEventCallback(() => {
    openDialog({
      listingGroups: eventListingGroups,
      listings: eventListings,
      canAddNewGroup: true,
      onMoveListingToGroup: buildAddToGroupModal,
      onClose: () => {
        closeDialog();
      },
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditMoveToGroup,
        onSelect: onLaunchDialogStableRef,
      },
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditMoveToGroup,
      });
    };
  }, [onLaunchDialogStableRef, registerCommand, unregisterCommand]);

  const actionItems = useMemo(() => {
    return [{ title: ContentId.MoveTo, onClick: onLaunchDialog }];
  }, [onLaunchDialog]);

  return {
    dropdown: (
      <PosDropdownItem key="MoveToGroup" onClick={onLaunchDialog}>
        <Stack alignItems="center" gap="m" width="full">
          <Content id={ContentId.MoveTo} />
        </Stack>
      </PosDropdownItem>
    ),
    actionItems,
  };
};
